// Pure toast logic shared by the ErrorMessage host. No React, no timers — just
// "what is this toast" decisions, so it is unit-testable in the node env.

export type ToastType = 'error' | 'notification' | 'success' | 'icon';

export interface ToastEntry {
  id: number;
  type: ToastType;
  message: string;
}

export const TOAST_AUTO_DISMISS_MS = 10000;

// Appends entry, or returns the SAME list reference if a toast with identical
// text is already showing (dedup). Never mutates the input.
export function addToastMessage(list: ToastEntry[], entry: ToastEntry): ToastEntry[] {
  if (list.some((m) => m.message === entry.message)) return list;
  return [...list, entry];
}

// ms until auto-dismiss, or null for sticky. Errors stay until closed on the
// deployed environments (staging/uat/production — users must not miss them),
// but auto-dismiss like every other toast on local dev (REACT_APP_HOST_ENV
// "development"), where sticky red toasts pile up during development. hostEnv
// is a parameter for testability; callers use the default.
export function toastDurationMs(
  type: ToastType,
  hostEnv: string | undefined = process.env.REACT_APP_HOST_ENV
): number | null {
  if (type !== 'error') return TOAST_AUTO_DISMISS_MS;
  return hostEnv === 'development' ? TOAST_AUTO_DISMISS_MS : null;
}

export function toastVariantClass(type: ToastType): string {
  if (type === 'error') return 'err';
  if (type === 'success') return 'ok';
  return 'info'; // notification + legacy icon
}

export function toastIcon(type: ToastType): string {
  if (type === 'error') return 'fa-solid fa-triangle-exclamation';
  if (type === 'success') return 'fa-solid fa-circle-check';
  return 'fa-solid fa-circle-info';
}

// Preserves the legacy behavior: a short string on an error becomes a friendly
// "Error! Errorcode: X" message.
export function normalizeErrorMessage(message: unknown, type: ToastType): unknown {
  return typeof message === 'string' && message.length < 10 && type === 'error'
    ? `Error! Errorcode: ${message}`
    : message;
}
